import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, map } from 'rxjs';

import {
    Action,
    CORRECT_CODENAME_SPLIT_LENGTH,
    CompanyAccess,
    Invitation,
    InvitationData,
    Member,
    Model,
    OrganisationRole,
    Permission,
} from '@shared/models';

import { DepreciationSettings } from '@/depreciations/models/depreciation-settings.model';
import { Account, History, Language, Organisation, OrganisationStatistics, ProductSubscription, Reconciliation } from '@/organisations/models';
import { Company } from '@/organisations/models/company.model';
import { SortLoop } from '@/silverfin-redirect/models';
import { SilverfinPeriod } from '@/silverfin-redirect/models/period.model';

import { AuthService } from './auth.service';

import { environment } from '@env';

@Injectable({
    providedIn: 'root',
})
export class OrganisationService {
    readonly activeOrganisation = new BehaviorSubject<Organisation | undefined>(undefined);
    readonly activeCompany = new BehaviorSubject<Company | undefined>(undefined);

    constructor(
        private http: HttpClient,
        private authService: AuthService,
    ) {}

    /**
     * Create a new organisation
     * @param {number} org - The {@link Organisation} data
     * @return {Observable<Organisation>} - The organisation data
     */
    createOrganisation(org: Organisation): Observable<Organisation> {
        return this.http
            .post<Organisation>(environment.endpoints.organisationList, org.serialize())
            .pipe(map<any, Organisation>((organisation) => new Organisation().deserialize(organisation)));
    }

    /**
     * Retrieves the details for a given organisation ID
     * @param {number} orgId - The {@link id} of the organisation
     * @return {Observable<Organisation>} - The organisation data
     */
    getOrganisation(orgId: number): Observable<Organisation> {
        return this.http
            .get<any>(environment.endpoints.organisationDetails(orgId))
            .pipe(map<any, Organisation>((organisation) => new Organisation().deserialize(organisation)));
    }

    getOrganisationStatistics(orgId: number): Observable<OrganisationStatistics> {
        return this.http
            .get<any>(environment.endpoints.organisationStatistics(orgId))
            .pipe(map<any, OrganisationStatistics>((stats) => new OrganisationStatistics().deserialize(stats)));
    }

    appendFormData(formData: FormData, obj: any, prefix: string = '') {
        if (prefix === '') {
            obj = obj.serialize();
        }
        if (!obj) {
            return;
        }
        Object.entries(obj).forEach(([key, value]: any) => {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            if (typeof value === 'object' && !(value instanceof File)) {
                this.appendFormData(formData, value, fullKey);
            } else if (value && key === 'avatar') {
                formData.append(fullKey, value as File);
            } else if (typeof value?.toString === 'function') {
                formData.append(fullKey, value.toString());
            }
        });
    }

    /**
     * Update the details for a given organisation
     * @param {data} organisation - The {@link Organisation} organisationData
     * @return {Observable<Organisation>} - The updated organisation
     */
    updateOrganisation(data: Organisation): Observable<Organisation> {
        let formData = new FormData();
        this.appendFormData(formData, data, '');
        return this.http
            .patch<any>(environment.endpoints.organisationDetails(data.id), formData)
            .pipe(map<any, Organisation>((organisation) => new Organisation().deserialize(organisation)));
    }

    getOrganisations(): Observable<Organisation[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationList)
            .pipe(map<any[], Organisation[]>((data) => data?.map((d) => new Organisation().deserialize(d)) ?? []));
    }

    getMembers(orgId: number): Observable<Member[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationMemberList(orgId))
            .pipe(map<any[], Member[]>((data) => data?.map((d) => new Member().deserialize(d)) ?? []));
    }

    updateMember(orgId: number, member: Member): Observable<Member> {
        return this.http
            .patch<void>(environment.endpoints.organisationMemberDetails(orgId, member.id), member.serialize())
            .pipe(map<any, Member>((data) => new Member().deserialize(data)));
    }

    deleteMember(orgId: number, memberId: number): Observable<void> {
        return this.http.delete<void>(environment.endpoints.organisationMemberDetails(orgId, memberId));
    }

    getRoles(orgId: number): Observable<OrganisationRole[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationRolesList(orgId))
            .pipe(map<any[], OrganisationRole[]>((data) => data?.map((d) => new OrganisationRole().deserialize(d)) ?? []));
    }

    createOrganisationRole(orgId: number, organisationRole: OrganisationRole): Observable<OrganisationRole> {
        return this.http
            .post<OrganisationRole>(environment.endpoints.organisationRolesList(orgId), organisationRole.serialize())
            .pipe(map((role) => new OrganisationRole().deserialize(role)));
    }

    updateOrganisationRole(orgId: number, organisationRole: OrganisationRole): Observable<OrganisationRole> {
        return this.http
            .patch<OrganisationRole>(
                environment.endpoints.organisationRoleDetails(orgId, organisationRole.id),
                organisationRole.serialize(),
            )
            .pipe(map((role) => new OrganisationRole().deserialize(role)));
    }

    deleteOrganisationRole(orgId: number, organisationRoleId: number): Observable<any> {
        return this.http.delete<any>(environment.endpoints.organisationRoleDetails(orgId, organisationRoleId));
    }

    getRolePermissions(): Observable<Permission[]> {
        return this.http
            .get<Permission[]>(environment.endpoints.rolePermissions)
            .pipe(map((permissions) => permissions.map((permission) => new Permission().deserialize(permission))));
    }

    getInvitations(orgId: number): Observable<Invitation[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationInvitationList(orgId))
            .pipe(map<any[], Invitation[]>((data) => data?.map((d) => new Invitation().deserialize(d)) ?? []));
    }

    addInvitation(orgId: number, data: InvitationData): Observable<Invitation> {
        return this.http
            .post<any>(environment.endpoints.organisationInvitationList(orgId), data)
            .pipe(map<any, Invitation>((inv) => new Invitation().deserialize(inv)));
    }

    resendInvitation(orgId: number, invitationId: number): Observable<void> {
        return this.http.get<void>(environment.endpoints.organisationInvitationResend(orgId, invitationId));
    }

    deleteInvitation(orgId: number, invitationId: number): Observable<void> {
        return this.http.delete<void>(environment.endpoints.organisationInvitationDetails(orgId, invitationId));
    }

    getCompanies(orgId: number, sfFirmId?: number, sfCompanyId?: number, withScenarios = false): Observable<Company[]> {
        let body = { 'sf_firm_id': sfFirmId, 'sf_company_id': sfCompanyId, 'scenarios': 1 } as any;
        if (sfFirmId === undefined) {
            delete body.sf_firm_id;
        }
        if (sfCompanyId === undefined) {
            delete body.sf_company_id;
        }
        if (!withScenarios) {
            delete body.scenarios;
        }

        const params = Object.values(body).length ? { params: body as Record<string, number> } : {};
        return this.http.get<Company[]>(environment.endpoints.organisationCompaniesList(orgId), params).pipe(
            map<any[], Company[]>((data) => {
                let companies = data.map((d) => new Company().deserialize(d)) ?? [];
                if ((this.authService.activeUser.getValue()?.isStaff ?? false) && !this.authService.adminMode.getValue()) {
                    companies = companies.filter((company) => this.authService.activeUser.getValue()?.isCompanyViewer(orgId, company.id));
                }
                return companies;
            }),
        );
    }

    updateCompany(orgId: number, company: Company) {
        return this.http
            .patch<Company>(environment.endpoints.organisationCompaniesDetails(orgId, company.id), company.serialize())
            .pipe(map((c) => new Company().deserialize(c)));
    }

    getCompanyFollowers(orgId: number, companyId: number): Observable<string[]> {
        return this.http.get<string[]>(environment.endpoints.organisationCompaniesFollowersList(orgId, companyId));
    }

    setCompanyFollowers(orgId: number, companyId: number, followers: number[]): Observable<void> {
        return this.http.post<void>(environment.endpoints.organisationCompaniesUpdateFollowersList(orgId, companyId), {
            members: followers,
        });
    }

    getCompanyAccess(orgId: number, companyId: number): Observable<CompanyAccess[]> {
        return this.http
            .get<CompanyAccess[]>(environment.endpoints.organisationCompaniesAccessList(orgId, companyId))
            .pipe(map<any[], CompanyAccess[]>((data) => data.map((cmpAccess) => new CompanyAccess().deserialize(cmpAccess))));
    }

    createCompanyAccess(orgId: number, companyId: number, access: CompanyAccess): Observable<CompanyAccess> {
        return this.http
            .post(environment.endpoints.organisationCompaniesAccessList(orgId, companyId), access.serialize())
            .pipe(map<any, CompanyAccess>((data) => new CompanyAccess().deserialize(data)));
    }

    updateCompanyAccess(orgId: number, companyId: number, access: CompanyAccess): Observable<CompanyAccess> {
        return this.http
            .patch(environment.endpoints.organisationCompaniesAccessDetails(orgId, companyId, access.id), access.serialize())
            .pipe(map<any, CompanyAccess>((data) => new CompanyAccess().deserialize(data)));
    }

    deleteCompanyAccess(orgId: number, companyId: number, accessId: number): Observable<void> {
        return this.http.delete<void>(environment.endpoints.organisationCompaniesAccessDetails(orgId, companyId, accessId));
    }

    getCompanySortLoopValues(orgId: number, companyId: number, periodId: number, reconcilationId: number, loopname: string) {
        let params: Record<string, string> = { loopname };
        return this.http
            .get<SortLoop>(environment.endpoints.organisationCompanySortLoop(orgId, companyId, periodId, reconcilationId), {
                params,
            })
            .pipe(map<any, SortLoop>((data) => new SortLoop().deserialize(data)));
    }

    updateCompanySortLoopValues(orgId: number, companyId: number, periodId: number, reconcilationId: number, sortLoop: SortLoop) {
        return this.http
            .put<SortLoop>(
                environment.endpoints.organisationCompanySortLoop(orgId, companyId, periodId, reconcilationId),
                sortLoop.serialize(),
            )
            .pipe(map<any, SortLoop>((data) => new SortLoop().deserialize(data)));
    }

    getMailPreview(orgId: number, notificationContent: string, language: Language, color: string): Observable<string> {
        return this.http.put<string>(environment.endpoints.organisationMailPreview(orgId), {
            'notification_content': notificationContent,
            'requested_language': language,
            color,
        });
    }

    userHasPermission(model: Model, action: Action): boolean {
        if (this.authService.activeUser.getValue() === undefined) {
            return false;
        }

        if (this.authService.adminMode.getValue()) {
            return true;
        }

        const includedInAnyActiveUserRole = this.authService.activeUser
            .getValue()
            ?.roles.filter((role) => role.organisationId === (this.activeOrganisation.getValue()?.id ?? -1))
            .map((userRole) => userRole.permissions)
            .map((permissions) =>
                permissions.map((permission) => {
                    let split = permission.split('_');
                    if (
                        split.length === CORRECT_CODENAME_SPLIT_LENGTH &&
                        Object.values(Model).includes(split[1] as Model) &&
                        Object.values(Action).includes(split[0] as Action)
                    ) {
                        // eslint-disable-next-line no-extra-parens
                        return (split[1] as Model) === model && (split[0] as Action) === action;
                    }
                    return false;
                }),
            );

        return includedInAnyActiveUserRole?.some((bools) => bools.includes(true)) ?? false;
    }

    getSubscriptions(orgId: number): Observable<ProductSubscription[]> {
        return this.http
            .get<ProductSubscription[]>(environment.endpoints.organisationSubscriptionList(orgId))
            .pipe(map<any[], ProductSubscription[]>((data) => data?.map((d) => new ProductSubscription().deserialize(d)) ?? []));
    }

    createSubscription(orgId: number, productId: number): Observable<ProductSubscription> {
        return this.http
            .post<ProductSubscription>(environment.endpoints.organisationSubscriptionList(orgId), { product: productId })
            .pipe(map((subscription) => new ProductSubscription().deserialize(subscription)));
    }

    deleteSubscription(orgId: number, subscriptionRoleId: number): Observable<any> {
        return this.http.delete<any>(environment.endpoints.organisationSubscriptionDetails(orgId, subscriptionRoleId));
    }

    getCompanyAccounts(orgId: number, companyId: number, periodId?: number, depreciation?: boolean): Observable<Account[]> {
        let params = periodId !== undefined ? { params: { period_id: periodId } as Record<string, number | boolean> } : {};
        if (depreciation && params.params !== undefined) {
            params.params.depreciation = depreciation;
        }
        return this.http
            .get<ProductSubscription[]>(environment.endpoints.organisationCompaniesAccounts(orgId, companyId), params)
            .pipe(map<any[], Account[]>((data) => data?.map((d) => new Account().deserialize(d)) ?? []));
    }

    getDepreciationSettings(orgId: number): Observable<DepreciationSettings> {
        return this.http
            .get(environment.endpoints.organisationDepreciationSettings(orgId))
            .pipe(map((data) => new DepreciationSettings().deserialize(data)));
    }

    getHistories(orgId: number, startDate: Date, endDate: Date): Observable<History[]> {
        let params: Record<string, string> = { ['start_date']: startDate.toISOString(), ['end_date']: endDate.toISOString() };
        return this.http
            .get<History[]>(environment.endpoints.organisationHistories(orgId), { params })
            .pipe(map<any[], History[]>((histories) => histories.map((d: History) => new History().deserialize(d))));
    }

    updateDepreciationSettings(
        orgId: number,
        body: Partial<{ enable_notifications: boolean; invoice_type: 'Code' | 'TransactionEntryNo'; allowed_file_types: string[] }>,
    ): Observable<DepreciationSettings> {
        return this.http
            .patch(environment.endpoints.organisationDepreciationSettings(orgId), body)
            .pipe(map((data) => new DepreciationSettings().deserialize(data)));
    }

    getCompanyReconciliations(orgId: number, companyId: number, workflowId: number, periodId: number): Observable<Reconciliation[]> {
        let params: Record<string, number> = { workflow_id: workflowId, period_id: periodId };
        return this.http
            .get<any[]>(environment.endpoints.organisationCompaniesReconciliations(orgId, companyId), { params: params })
            .pipe(
                map<any[], Reconciliation[]>(
                    (reconciliations) => reconciliations?.map((reconciliation) => new Reconciliation().deserialize(reconciliation)),
                ),
            );
    }

    getCompanyPeriods(orgId: number, firmId:number, companyId: number): Observable<SilverfinPeriod[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationCompaniesPeriods(orgId, firmId, companyId))
            .pipe(
                map<any[], SilverfinPeriod[]>((periods) =>
                    periods?.map((p) => ({
                        id: p.id,
                        endDate: (p as any)['end_date'] ? new Date((p as any)['end_date']) : undefined,
                        startDate: (p as any)['start_date'] ? new Date((p as any)['start_date']) : undefined,
                    })) ?? [],
                ),
            );
    }
}
